/* Đề bài (việc cần tập trung) + các hàng hướng dẫn mở/đóng của một Product. */
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Icon } from "@/components/icon"
import { learningTypeLabel } from "@/components/learning"
import { formatDateTime } from "@/lib/format"
import { ExemplarCard, NavRow, StatusBadge } from "./shared"

const arr = (v: any) => (Array.isArray(v) ? v : [])

export function ProductFocus({ brief, actionLabel, onAction }: { brief: any; actionLabel: string; onAction: () => void }) {
  return (
    <Card className="border-orange/40 bg-[color-mix(in_oklch,var(--ds-soft),white_40%)]">
      <CardContent className="flex flex-col gap-4">
        <p className="text-sm font-bold tracking-[0.12em] text-orange-strong uppercase">Đề bài</p>
        {brief ? (
          <>
            <h2 className="font-heading text-xl leading-snug font-bold text-ink sm:text-2xl">{brief.challenge}</h2>
            <p className="text-base text-ink">
              <b>Kết quả cần tạo:</b> {brief.outcome}
            </p>
          </>
        ) : (
          <p className="text-base text-muted-foreground">Mentor đang hoàn thiện đề bài cho Product này.</p>
        )}
        <Button size="lg" className="self-start" onClick={onAction}>
          {actionLabel} <Icon name="arrow-right" className="size-4" />
        </Button>
      </CardContent>
    </Card>
  )
}

function NumberedList({ items, ordered }: { items: string[]; ordered?: boolean }) {
  const List = ordered ? "ol" : "ul"
  return (
    <List className="flex flex-col gap-2.5">
      {items.map((item, i) => (
        <li key={`${i}-${item}`} className="flex gap-3">
          <span className="grid size-7 shrink-0 place-items-center rounded-full bg-soft text-sm font-bold text-accent-foreground">{i + 1}</span>
          <span className="pt-0.5 text-ink">{item}</span>
        </li>
      ))}
    </List>
  )
}

function History({ submissions }: { submissions: any[] }) {
  if (!submissions.length) return <p className="text-muted-foreground">Con chưa gửi phiên bản nào.</p>
  return (
    <div className="flex flex-col gap-3">
      {submissions.map((s) => (
        <article key={s.id} className="flex flex-col gap-2 rounded-2xl bg-sand p-4">
          <header className="flex flex-wrap items-center gap-2">
            <strong className="text-ink">Phiên bản {s.version}</strong>
            <span className="text-sm text-muted-foreground">{formatDateTime(s.updated_at)}</span>
            <span className="ml-auto">
              <StatusBadge status={s.status} />
            </span>
          </header>
          {arr(s.assessments).map((a: any) => (
            <div key={a.id} className="flex flex-col gap-1 rounded-xl bg-card p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-ink">
                  {a.assessment_kind === "trial" ? "Chấm thử" : "Chấm thật"} lần {a.attempt}
                </span>
                {a.normalized_score != null && <Badge variant={a.assessment_kind === "trial" ? "default" : "success"}>{Math.round(a.normalized_score * 100)} điểm</Badge>}
              </div>
              {a.result?.overall_feedback && <p className="text-sm text-muted-foreground">{a.result.overall_feedback}</p>}
              {a.result?.next_step && <p className="text-sm font-semibold text-ink">{a.result.next_step}</p>}
            </div>
          ))}
        </article>
      ))}
    </div>
  )
}

function RubricDetails({ criteria }: { criteria: any[] }) {
  return (
    <div className="flex flex-col gap-5">
      {criteria.map((c) => (
        <section key={c.id} className="flex flex-col gap-2">
          <h4 className="font-heading font-semibold text-ink">
            {c.position}. {c.name}
          </h4>
          {c.description && <p className="text-sm text-muted-foreground">{c.description}</p>}
          {arr(c.indicators).map((ind: any) => (
            <IndicatorLevels key={ind.id} label={ind.label} levels={arr(ind.levels)} />
          ))}
        </section>
      ))}
    </div>
  )
}

function IndicatorLevels({ label, levels }: { label: string; levels: any[] }) {
  return (
    <details className="group rounded-xl bg-sand">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 font-semibold text-ink">
        <span className="flex-1">{label}</span>
        <Icon name="chevron-down" className="size-4 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="flex flex-col gap-1.5 px-3 pb-3">
        {levels.map((lv) => (
          <div key={lv.code} className="flex flex-col gap-0.5 rounded-lg bg-card p-2.5 sm:flex-row sm:gap-3">
            <b className="shrink-0 text-sm text-orange-strong sm:w-32">{lv.label}</b>
            <span className="text-sm text-ink">{lv.description}</span>
          </div>
        ))}
      </div>
    </details>
  )
}

export function ProductGuide({ brief, submissions, rubric, exemplars, slug }: { brief: any; submissions: any[]; rubric: any; exemplars: any[]; slug: string }) {
  const instructions: string[] = arr(brief?.instructions)
  const completion: string[] = arr(brief?.completion)
  const requirements: any[] = arr(brief?.learning_requirements)
  const criteria: any[] = arr(rubric?.criteria)
  // Brief cũ chỉ có một chuỗi `example`; brief mới có mảng `examples`.
  const examples: string[] = arr(brief?.examples).length ? brief.examples : brief?.example ? [brief.example] : []
  return (
    <div className="flex flex-col gap-3">
      {exemplars.length > 0 && (
        <NavRow icon="sparkles" title="Bài mẫu tham khảo" hint={`${exemplars.length} bài`}>
          {exemplars.map((e) => (
            <ExemplarCard key={e.id} exemplar={e} slug={slug} />
          ))}
        </NavRow>
      )}
      {examples.length > 0 && (
        <NavRow icon="lightbulb" title="Ví dụ mẫu" hint={`${examples.length} ví dụ`}>
          <NumberedList items={examples} />
        </NavRow>
      )}
      {instructions.length > 0 && (
        <NavRow icon="list" title="Làm như thế nào?" hint={`${instructions.length} bước`}>
          <NumberedList items={instructions} ordered />
        </NavRow>
      )}
      {completion.length > 0 && (
        <NavRow icon="check-circle" title="Như thế nào là làm xong?" hint={`${completion.length} điều`}>
          <ul className="flex flex-col gap-2.5">
            {completion.map((item, i) => (
              <li key={`${i}-${item}`} className="flex gap-3">
                <Icon name="check-circle" className="mt-0.5 size-5 shrink-0 text-success" />
                <span className="text-ink">{item}</span>
              </li>
            ))}
          </ul>
        </NavRow>
      )}
      {requirements.length > 0 && (
        <NavRow icon="graduation" title="Cần học gì?" hint={`${requirements.length} mục`}>
          <div className="grid gap-3 sm:grid-cols-2">
            {requirements.map((r) => (
              <article key={`${r.type}:${r.name}`} className="flex flex-col gap-1 rounded-2xl bg-sand p-4">
                <span className="text-sm font-bold tracking-wide text-orange-strong uppercase">{learningTypeLabel[r.type] || r.type}</span>
                <b className="text-ink">{r.name}</b>
                <p className="text-sm text-muted-foreground">{r.description}</p>
              </article>
            ))}
          </div>
        </NavRow>
      )}
      <NavRow icon="clock" title="Lịch sử phiên bản & chấm bài" hint={`${submissions.length} phiên bản`}>
        <History submissions={submissions} />
      </NavRow>
      {criteria.length > 0 && (
        <NavRow icon="target" title="Rubric chấm bài" hint={`${criteria.length} tiêu chí`}>
          <RubricDetails criteria={criteria} />
        </NavRow>
      )}
    </div>
  )
}
